import { useEffect, useRef } from "react";

import { formatReceivedAt } from "../../../utils/formatReceivedAt";

import styles from "./LeadWhatsApp.module.css";

export const FROM_PHONE_LABEL = "Desde el teléfono";

export const MEDIA_LABELS = {
  placeholder: "Archivo del historial no disponible",
  image: "Imagen",
  document: "Documento",
  audio: "Audio",
  video: "Video",
  sticker: "Sticker",
  unsupported: "Mensaje no soportado",
};

export const STATUS_LABELS = {
  queued: "En cola",
  sent: "Enviado",
  delivered: "Entregado",
  read: "Leído",
  failed: "No se pudo enviar",
};

const STATUS_GLYPHS = {
  queued: "…",
  sent: "✓",
  delivered: "✓✓",
  read: "✓✓",
  failed: "!",
};

const MessageStatus = ({ message }) => {
  const status = message?.status;
  if (!status || message?.direction !== "outbound") return null;
  const label = STATUS_LABELS[status] || status;
  const failed = status === "failed";
  return (
    <span
      className={`${styles.messageStatus} ${status === "read" ? styles.messageStatusRead : ""} ${
        failed ? styles.messageStatusFailed : ""
      }`}
      title={failed && message.error_detail ? message.error_detail : label}
      aria-label={label}
    >
      {STATUS_GLYPHS[status] || ""}
    </span>
  );
};

export const renderMessageContent = (message) => {
  const type = message?.message_type || "text";
  const body = message?.body || "";
  if (type === "text") return { label: null, body };
  if (type === "template") {
    return { label: `Plantilla · ${message?.template_name || ""}`.trim(), body };
  }
  return { label: MEDIA_LABELS[type] || MEDIA_LABELS.unsupported, body };
};

const WhatsAppMessageList = ({
  messages = [],
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  renderMedia,
}) => {
  const listRef = useRef(null);
  const lastMessageIdRef = useRef(null);

  useEffect(() => {
    const lastMessageId = messages.length ? messages[messages.length - 1]?.id : null;
    if (lastMessageId === lastMessageIdRef.current) return;
    lastMessageIdRef.current = lastMessageId;
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages]);

  if (!messages.length) {
    return (
      <div className={styles.emptyState}>
        Todavía no hay mensajes en esta conversación.
      </div>
    );
  }

  return (
    <div className={styles.messageList} ref={listRef}>
      {hasMore && (
        <button
          type="button"
          className={styles.loadOlderButton}
          onClick={() => onLoadOlder?.()}
          disabled={loadingOlder}
        >
          {loadingOlder ? "Cargando..." : "Ver mensajes anteriores"}
        </button>
      )}
      {messages.map((message) => {
        const outbound = message.direction === "outbound";
        const { label, body } = renderMessageContent(message);
        const media = typeof renderMedia === "function" ? renderMedia(message) : null;
        return (
          <div
            key={message.id}
            className={`${styles.messageRow} ${outbound ? styles.messageRowOutbound : ""}`}
          >
            <div
              className={`${styles.messageBubble} ${
                outbound ? styles.messageBubbleOutbound : styles.messageBubbleInbound
              } ${message.status === "failed" ? styles.messageBubbleFailed : ""}`}
            >
              {outbound && (message.sent_by_name || message.sent_from_app) && (
                <span className={styles.messageSender}>
                  {message.sent_from_app ? FROM_PHONE_LABEL : message.sent_by_name}
                </span>
              )}
              {label && <span className={styles.messageLabel}>{label}</span>}
              {media}
              {body && <p className={styles.messageBody}>{body}</p>}
              <span className={styles.messageMeta}>
                <span className={styles.messageTime}>
                  {formatReceivedAt(message.timestamp)}
                </span>
                <MessageStatus message={message} />
              </span>
              {message.status === "failed" && message.error_detail && (
                <span className={styles.messageError}>{message.error_detail}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WhatsAppMessageList;
